import React, { useEffect, useMemo, useCallback, useRef } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { getFacetTheme } from "../assets/facetTheme";
import { formatPct } from "../lib/format";
import "../styling/Facets.css";

ChartJS.register(ArcElement, Tooltip, Legend);

const FIELD_ORDER = {
	clinicalResponse: ["Complete response", "Partial response", "Stable disease", "Progressive disease", "N/A"],
	reasonDisc: [
		"Planned stop with continued benefit",
		"Progressive disease",
		"Adverse effects",
		"Death",
		"Other",
		"N/A"
	],
	aeGrade: ["1", "2", "3", "4", "Unknown"],
	hasDiscontinuationDueToAE: ["Yes", "No"],
	sex: ["Male", "Female", "N/A"],
	biomarkerType: [
		"Protein expression",
		"Mutation",
		"Copy number alteration",
		"Mutational signature",
		"Fusion"
	],
};

export default function FacetPie({
	data = [],
	loading = false,
	filters,
	setFilters,
	field,
	category
}) {
	const chartRef = useRef(null);
	const containerRef = useRef(null);
	const theme = getFacetTheme(category);
	const tooltipClass = `pie-tooltip-${field}`;

	useEffect(() => {
		return () => {
			document
				.querySelectorAll(`.${tooltipClass}`)
				.forEach((el) => el.remove());
		};
	}, [tooltipClass]);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;

		let frameId = null;

		const ro = new ResizeObserver(() => {
			if (frameId) cancelAnimationFrame(frameId);

			frameId = requestAnimationFrame(() => {
				const chart = chartRef.current;

				if (!chart || !chart.canvas || chart._destroyed) return;

				chart.resize();
			});
		});

		ro.observe(el);

		return () => {
			if (frameId) cancelAnimationFrame(frameId);
			ro.disconnect();
		};
	}, []);

	const sortedData = useMemo(() => {
		if (!FIELD_ORDER[field]) {
			return data;
		}

		const order = FIELD_ORDER[field];
		return [...data].sort((a, b) => {
			const indexA = order.indexOf(a.name);
			const indexB = order.indexOf(b.name);

			if (indexA !== -1 && indexB !== -1) {
				return indexA - indexB;
			}

			if (indexA === -1) return 1;
			if (indexB === -1) return -1;

			return 0;
		});
	}, [data, field]);

	const selectedSet = useMemo(
		() => filters.get(field) || new Set(),
	[filters, field]);

	const labels = useMemo(() => sortedData.map((d) => d.name), [sortedData]);
	const counts = useMemo(() => sortedData.map((d) => d.count), [sortedData]);

	const colors = useMemo(() => {
		const step = labels.length < 4 ? 2 : 1;
		const palette = theme.palette;

		return labels.map((lab, i) =>
			!selectedSet.size || selectedSet.has(lab)
				? palette[(i * step) % palette.length]
				: theme.dim
		);
	}, [labels, selectedSet, theme]);

	const offsets = useMemo(() => {
		return labels.map((lab) =>
			selectedSet.size && selectedSet.has(lab) ? 8 : 0
		);
	}, [labels, selectedSet]);

	const handlePickLabel = useCallback(
		(label) => {
			setFilters((prev) => {
				const next = new Map(prev);
				const curr = new Set(next.get(field) || []);
				if (curr.has(label)) curr.delete(label);
				else curr.add(label);
				next.set(field, curr.size > 0 ? curr : new Set());
				return next;
			});
		},
		[field, setFilters]
	);

	const handleLegendEnter = useCallback((i) => {
		const chart = chartRef.current;
		if (!chart || chart._destroyed) return;
		chart.setActiveElements([{ datasetIndex: 0, index: i }]);
		chart.tooltip.setActiveElements([{ datasetIndex: 0, index: i }]);
		chart.update("none");
	}, []);

	const handleLegendLeave = useCallback(() => {
		const chart = chartRef.current;
		if (!chart || chart._destroyed) return;
		chart.setActiveElements([]);
		chart.tooltip.setActiveElements([]);
		chart.update("none");
	}, []);

	const chartData = useMemo(
		() => ({
			labels,
			datasets: [
				{
					data: counts,
					backgroundColor: colors,
					borderColor: "#fff",
					borderWidth: 2,
					offset: offsets,
					hoverOffset: 10,
				},
			],
		}),
		[labels, counts, colors, offsets]
	);

	const chartOptions = useMemo(() => ({
		maintainAspectRatio: false,
		animation: false,
		layout: { padding: { top: 5, bottom: 5 } },

		onClick: (_evt, active) => {
			if (active?.length) {
				const label = labels[active[0].index];
				handlePickLabel(label);
			}
		},

		onHover: (e, a) => {
			e.native.target.style.cursor = a.length ? "pointer" : "default";
		},

		plugins: {
			legend: { display: false },

			tooltip: {
				enabled: false,
				external: (context) => {
					const { chart, tooltip } = context;

					let el = document.querySelector(`.${tooltipClass}`);
					if (!el) {
						el = document.createElement("div");
						el.className = tooltipClass;
						Object.assign(el.style, {
							position: "fixed",
							pointerEvents: "none",
							background: "rgba(0, 0, 0, 0.8)",
							color: "#fff",
							padding: "6px 10px",
							borderRadius: "4px",
							fontSize: "12px",
							whiteSpace: "nowrap",
							zIndex: "9999",
							transition: "opacity 0.15s",
							opacity: "0",
						});
						document.body.appendChild(el);
					}

					if (tooltip.opacity === 0 || !tooltip.dataPoints?.length) {
						el.style.opacity = "0";
						return;
					}

					const dp = tooltip.dataPoints[0];
					const item = sortedData[dp.dataIndex];
					const val = item?.count ?? 0;
					const pct = formatPct(item?.percentage ?? 0);
					el.textContent = `${dp.label}: ${val} (${pct})`;

					const rect = chart.canvas.getBoundingClientRect();
					el.style.opacity = "1";
					el.style.left = rect.left + tooltip.caretX + "px";
					el.style.top = rect.top + tooltip.caretY + "px";
					el.style.transform = "translate(-50%, -120%)";
				},
			},
		},
	}), [labels, sortedData, handlePickLabel, tooltipClass]);

	const isCompact = labels.length > 5;

	if (loading) {
		return (
			<div className="facet-loading">
				<div className="spinner"></div>
				<div className="loading-text">Loading chart...</div>
			</div>
		);
	}

	return (
		<div className="chart-fill pie facet-enter">
			<div className="pie-container" ref={containerRef}>
				<Pie ref={chartRef} data={chartData} options={chartOptions} />
			</div>
			<div className="pie-legend-container">
				{labels.map((lab, i) => (
					<div
						className="pie-legend-container-element"
						key={lab}
						onClick={() => handlePickLabel(lab)}
						onMouseEnter={() => handleLegendEnter(i)}
						onMouseLeave={handleLegendLeave}
						style={{
							fontSize: isCompact ? 11 : 13,
						}}
						title={lab}
					>
						<span
							className="pie-legend-container-element-box"
							style={{
								width: isCompact ? 8 : 10,
								height: isCompact ? 8 : 10,
								backgroundColor: colors[i],
							}}
						/>
						<span className="pie-legend-container-element-text">
							{lab.length > 15 ? lab.substring(0, 15) + "..." : lab}
						</span>
					</div>
				))}
			</div>
		</div>
	);
}